/**
 * Разворот: ловить смену направления.
 *
 * Идеи против движения рискованнее трендовых: рынок может «перепроданным»
 * оставаться долго. Поэтому у каждой — событие разворота (выход из зоны,
 * свеча отказа, расхождение), а не одно лишь состояние крайности.
 */
import { above, against, bb, below, close, divergenceParams, happened, ind, inZone, level, mirror, number, previous, rsi, rvol, spec, stochK, tripleParams, weight, } from './kit.js';
export const REVERSAL_TEMPLATES = [
    {
        id: 'mean-reversion',
        name: 'Возврат к среднему',
        icon: '🔄',
        category: 'reversal',
        popular: true,
        hint: 'Вход на выходе из перекупленности или перепроданности — против растянутого движения.',
        steps: ['RSI вышел из крайней зоны', 'Цена за границей полосы Боллинджера'],
        keywords: ['rsi', 'перепроданность', 'reversion'],
        passport: {
            level: 'novice',
            market: 'range',
            horizon: 'day',
            style: 'counter-trend',
            timeframe: '1H',
            idea: 'Цена ушла слишком далеко от средней, RSI был в крайней зоне и начал из неё выходить — растяжение заканчивается.',
            why: 'Резкие движения часто перегибают: после паники и эйфории цена возвращается к средней.',
            fails: 'В сильном тренде: перепроданность может длиться неделями, и каждый «возврат» оказывается остановкой перед новым падением.',
            tip: 'Лучше всего в боковике. В сильном тренде берите только сигналы по ходу тренда.',
        },
        build: (o) => spec(o, 'Возврат к среднему', mirror((side) => ({
            context: [],
            trigger: side.cross(rsi, number(level(side, 30))),
            confirmations: [weight('c1', against(side)(close, bb(side.sign > 0 ? 'lower' : 'upper')), 100)],
        })), 0),
    },
    {
        id: 'reversal-swing',
        name: 'Разворот от экстремума',
        icon: '🔄',
        category: 'reversal',
        hint: 'Цена у края диапазона, свеча отказывается идти дальше.',
        steps: ['Цена у края диапазона', 'Пин-бар отказа', 'RSI в крайней зоне', 'Объём'],
        keywords: ['разворот', 'пин-бар', 'экстремум'],
        passport: {
            level: 'novice',
            market: 'range',
            horizon: 'swing',
            style: 'counter-trend',
            timeframe: '4H',
            idea: 'Цена дошла до края недавнего диапазона, и свеча с длинной тенью показала: дальше не пускают.',
            why: 'Длинная тень — след того, что цену пытались продавить, но встречные заявки её вернули.',
            fails: 'Когда край диапазона пробивают всерьёз: следующая свеча закрывается за ним, и отказ оказывается паузой.',
            tip: 'Ставьте защиту за кончиком тени: если её пробили — идея сломалась.',
        },
        build: (o) => spec(o, 'Разворот от экстремума', mirror((side) => ({
            context: [inZone(side, 30)],
            trigger: happened(ind('pin_bar', { factor: 2 }, side.way)),
            confirmations: [
                weight('c1', side.sign > 0 ? below(rsi, number(40)) : above(rsi, number(60)), 60),
                weight('c2', above(rvol, number(1.2)), 40),
            ],
        })), 40),
    },
    {
        id: 'bollinger-reversion',
        name: 'Возврат в канал',
        icon: '🎈',
        category: 'reversal',
        hint: 'Цена вышла за полосу Боллинджера и вернулась обратно — растяжение закончилось.',
        steps: ['Цена вернулась внутрь полосы', 'RSI подтверждает крайность'],
        keywords: ['боллинджер', 'канал', 'bollinger'],
        passport: {
            level: 'novice',
            market: 'range',
            horizon: 'swing',
            style: 'counter-trend',
            timeframe: '4H',
            idea: 'Полосы Боллинджера обводят обычный размах цены. Выход за полосу — перегиб, возврат внутрь — его конец.',
            why: 'Большую часть времени цена живёт внутри полос; выход за них редко бывает долгим.',
            fails: 'В начале сильного движения цена «едет» по полосе и возвращается внутрь лишь на мгновение.',
            tip: 'Если полосы резко расширяются — это не возврат, а новый тренд. Пропускайте такие сигналы.',
        },
        build: (o) => spec(o, 'Возврат в канал', mirror((side) => ({
            context: [],
            trigger: side.cross(close, bb(side.sign > 0 ? 'lower' : 'upper')),
            confirmations: [weight('c1', side.sign > 0 ? below(rsi, number(40)) : above(rsi, number(60)), 100)],
        })), 0),
    },
    {
        id: 'engulfing-reversal',
        name: 'Разворот на поглощении',
        icon: '🕯',
        category: 'reversal',
        hint: 'Свеча перекрывает предыдущую в противоположную сторону у края диапазона.',
        steps: ['Цена у края диапазона', 'Поглощение в нашу сторону', 'Объём подтверждает'],
        keywords: ['поглощение', 'engulfing', 'свеча'],
        passport: {
            level: 'novice',
            market: 'range',
            horizon: 'swing',
            style: 'counter-trend',
            timeframe: '4H',
            idea: 'У края диапазона одна свеча целиком «съела» предыдущую в обратную сторону — перехватили инициативу.',
            why: 'Поглощение показывает, что за одну свечу сторона не просто остановила движение, а развернула его.',
            fails: 'В середине диапазона поглощения ничего не значат — поэтому здесь нужна цена у края.',
            tip: 'Чем больше объём на поглощении, тем надёжнее: слабое поглощение — просто колебание.',
        },
        build: (o) => spec(o, 'Разворот на поглощении', mirror((side) => ({
            context: [inZone(side, 35)],
            trigger: happened(ind('engulfing', { factor: 1 }, side.way)),
            confirmations: [weight('c1', above(rvol, number(1.5)), 100)],
        })), 0),
    },
    {
        id: 'divergence-classic',
        name: 'Классическая дивергенция',
        icon: '📐',
        category: 'reversal',
        hint: 'Цена обновила экстремум, а импульс — нет: движение выдыхается.',
        steps: ['Цена ставит новый экстремум', 'RSI его не подтверждает', 'RSI в крайней зоне и объём'],
        keywords: ['дивергенция', 'divergence', 'rsi', 'разворот', 'импульс'],
        passport: {
            level: 'skilled',
            market: 'any',
            horizon: 'swing',
            style: 'counter-trend',
            timeframe: '4H',
            idea: 'Цена сделала новый минимум (или максимум), а RSI — нет: движение идёт по инерции, сил за ним меньше.',
            why: 'Импульс обычно слабеет раньше, чем разворачивается цена: расхождение — ранний признак усталости.',
            fails: 'В сильном тренде дивергенций бывает несколько подряд, а цена продолжает идти.',
            tip: 'Дождитесь слома структуры после дивергенции — это отсекает большую часть ложных.',
        },
        build: (o) => spec(o, 'Классическая дивергенция', mirror((side) => ({
            context: [],
            trigger: happened(ind('divergence', divergenceParams, side.way)),
            confirmations: [
                weight('c1', side.sign > 0 ? below(rsi, number(40)) : above(rsi, number(60)), 60),
                weight('c2', above(rvol, number(1.2)), 40),
            ],
        })), 0),
    },
    {
        id: 'divergence-triple',
        name: 'Тройная дивергенция',
        icon: '🔥',
        category: 'reversal',
        hint: 'Расхождение на трёх экстремумах подряд: импульс слабеет на каждом.',
        steps: ['Три экстремума подряд', 'Импульс слабеет на каждом', 'Объём участвует'],
        keywords: ['тройная', 'дивергенция', 'разворот', 'импульс'],
        passport: {
            level: 'pro',
            market: 'any',
            horizon: 'swing',
            style: 'counter-trend',
            timeframe: '4H',
            idea: 'Три вершины (или впадины) подряд, и на каждой импульс слабее прежней — движение явно выдыхается.',
            why: 'Одна дивергенция бывает случайной, три подряд — это устойчивое ослабление.',
            fails: 'Редкий сигнал: неделями может не быть ни одного. И даже тройная не гарантирует разворот в тренде-монстре.',
            tip: 'Берите как сильный повод для внимания, а вход ищите по слому структуры.',
        },
        build: (o) => spec(o, 'Тройная дивергенция', mirror((side) => ({
            context: [],
            trigger: happened(ind('divergence', tripleParams, `triple_${side.way}`)),
            confirmations: [
                weight('c1', side.sign > 0 ? below(rsi, number(40)) : above(rsi, number(60)), 60),
                weight('c2', above(rvol, number(1.2)), 40),
            ],
        })), 0),
    },
    /* ---------------------------------------------------------- новые */
    {
        id: 'double-oversold',
        name: 'Двойная перепроданность',
        icon: '🧲',
        category: 'reversal',
        hint: 'Stochastic только что был в крайней зоне, и RSI выходит из своей — два осциллятора согласны.',
        steps: ['Stochastic недавно был в крайней зоне', 'RSI вышел из крайней зоны', 'Объём'],
        keywords: ['rsi', 'stochastic', 'стохастик', 'перепроданность', 'двойной'],
        passport: {
            level: 'novice',
            market: 'range',
            horizon: 'day',
            style: 'counter-trend',
            timeframe: '1H',
            idea: 'Два разных осциллятора одновременно говорят «перегиб»: быстрый Stochastic был в крайности, медленный RSI выходит из неё.',
            why: 'Один осциллятор ошибается часто; согласие двух отсекает часть случайных сигналов.',
            fails: 'В сильном тренде оба могут подолгу лежать в крайней зоне.',
            tip: 'Ищите у края диапазона — тогда совпадение трёх признаков.',
        },
        build: (o) => spec(o, 'Двойная перепроданность', mirror((side) => ({
            context: [side.sign > 0 ? below(previous(stochK, 2), number(25)) : above(previous(stochK, 2), number(75))],
            trigger: side.cross(rsi, number(level(side, 35))),
            confirmations: [weight('c1', above(rvol, number(1.2)), 50), weight('c2', inZone(side, 40), 50)],
        })), 0),
    },
    {
        id: 'volume-climax',
        name: 'Кульминация объёма',
        icon: '🌋',
        category: 'reversal',
        hint: 'Паника или эйфория на огромном объёме, а затем свеча разворота — движение выгорело.',
        steps: ['Недавно объём втрое выше обычного', 'Цена у края диапазона', 'Поглощение в нашу сторону'],
        keywords: ['кульминация', 'climax', 'капитуляция', 'объём', 'разворот'],
        passport: {
            level: 'skilled',
            market: 'any',
            horizon: 'day',
            style: 'counter-trend',
            timeframe: '1H',
            idea: 'Все, кто хотел выйти, вышли разом — объём взрывается. Сразу после этого свеча разворачивает движение.',
            why: 'Кульминация забирает последних продавцов (или покупателей): дальше двигать цену некому.',
            fails: 'Если объём высокий из-за новости, а не из-за паники, — движение может продолжиться.',
            tip: 'Самые сильные кульминации случаются у круглых уровней и после длинной серии свечей в одну сторону.',
        },
        build: (o) => spec(o, 'Кульминация объёма', mirror((side) => ({
            context: [above({ kind: 'recent', of: rvol, bars: 3 }, number(3)), inZone(side, 30)],
            trigger: happened(ind('engulfing', { factor: 1 }, side.way)),
            confirmations: [weight('c1', above({ kind: 'recent', of: rvol, bars: 3 }, number(4)), 60), weight('c2', above(rvol, number(1.5)), 40)],
        })), 0),
    },
];
//# sourceMappingURL=reversal.js.map